/**
 * Derives the coach's coachee list, and the per-coachee session groupings shown
 * on a coachee's page, from records the coach can already read.
 *
 * A coachee joins the coach's list once they shortlist the coach for a chemistry
 * call (`chemistry`) or pick them (`chosen`). `pending` means matched but not yet
 * shortlisted and `declined` means the relationship ended, so neither is a
 * coaching relationship the coach should be working from.
 */
import type { CoacheeNameFields } from '@efdigital/ll-addon-coaching';
import {
  coacheeInitials,
  resolveConfirmedDurationMinutes,
} from '@efdigital/ll-addon-coaching';
import {
  isCancelled,
  isHistory,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-lifecycle';
import type {
  CoachingPartnershipStatus,
  CoachingSessionStatus,
  CoachingSessionType,
} from '@efdigital/ll-addon-data';
import { UPCOMING_SCHEDULED_STATUSES } from '@efdigital/ll-addon-data';

import {
  coachSessionNeedsAction,
  type SessionActionFields,
} from './session-current-categories.ts';

/** Partnership statuses that make a learner one of this coach's coachees. */
export const COACH_COACHEE_PARTNERSHIP_KEYS: CoachingPartnershipStatus[] = [
  'chemistry',
  'chosen',
];

/**
 * Statuses counted as a session the coach delivered.
 *
 * A coachee no-show still used the coach's time, so it counts. Chemistry is
 * excluded from the dashboard "Sessions delivered" stat — it has its own status.
 */
export const COACH_DELIVERED_KEYS: CoachingSessionStatus[] = [
  'attended',
  'no_show_coachee',
];

/** The partnership fields this module reads, so the logic is testable without records. */
export type CoacheePartnershipFields = {
  user: string;
  status: CoachingPartnershipStatus;
};

/** The session fields this module reads. */
export type CoacheeSessionFields = SessionActionFields & {
  sessionType: CoachingSessionType;
  /** ISO 8601 datetime */
  startTime: string;
  /** ISO 8601 datetime */
  endTime: string;
  /** Coachee user id, denormalized onto the session. */
  user: string;
  confirmedDurationMinutes: number | null;
};

/** Coachee name PII, as returned by `GET /coach/coachees`. */
export type CoacheeName = CoacheeNameFields & { userId: string };

/** One row in the coach's coachee list, with everything both pages need. */
export type CoacheeListEntry<
  P extends CoacheePartnershipFields,
  S extends CoacheeSessionFields,
> = {
  coacheeUserId: string;
  coachee?: CoacheeName;
  /** Falls back to the user id when name PII could not be resolved. */
  displayName: string;
  initials: string;
  /** `chosen` wins when a coachee is chosen on one plan and in chemistry on another. */
  status: CoachingPartnershipStatus;
  /** More than one only when a coachee works with this coach on several plans. */
  partnerships: P[];
  /** Sessions this coach holds with this coachee, in whatever order they arrived. */
  sessions: S[];
  hasChemistrySession: boolean;
};

/** Ordering used to pick the status shown when a coachee has several partnerships. */
const STATUS_RANK: Record<CoachingPartnershipStatus, number> = {
  chosen: 4,
  chemistry: 3,
  pending: 2,
  declined: 1,
  reassigned: 0,
};

/** The furthest-along status of a coachee's partnerships with one coach. */
export function highestPartnershipStatus(
  statuses: CoachingPartnershipStatus[]
): CoachingPartnershipStatus {
  return statuses.reduce(
    (highest, status) =>
      STATUS_RANK[status] > STATUS_RANK[highest] ? status : highest,
    statuses[0]!
  );
}

/**
 * Groups the coach's partnerships into one entry per coachee, joined to their
 * name PII and their sessions with this coach, sorted by display name.
 */
export function buildCoacheeList<
  P extends CoacheePartnershipFields,
  S extends CoacheeSessionFields,
>(
  partnerships: P[],
  sessions: S[],
  coachees: CoacheeName[]
): CoacheeListEntry<P, S>[] {
  const namesByUserId = new Map(
    coachees.map((coachee) => [coachee.userId, coachee])
  );
  const partnershipsByUserId = new Map<string, P[]>();

  for (const partnership of partnerships) {
    const existing = partnershipsByUserId.get(partnership.user);
    if (existing) {
      existing.push(partnership);
    } else {
      partnershipsByUserId.set(partnership.user, [partnership]);
    }
  }

  const entries: CoacheeListEntry<P, S>[] = [];

  for (const [coacheeUserId, coacheePartnerships] of partnershipsByUserId) {
    const coachee = namesByUserId.get(coacheeUserId);
    const coacheeSessions = sessions.filter(
      (session) => session.user === coacheeUserId
    );

    entries.push({
      coacheeUserId,
      coachee,
      displayName: coachee?.displayName?.trim() || coacheeUserId,
      initials: coacheeInitials(coachee, coacheeUserId),
      status: highestPartnershipStatus(
        coacheePartnerships.map((partnership) => partnership.status)
      ),
      partnerships: coacheePartnerships,
      sessions: coacheeSessions,
      hasChemistrySession: coacheeSessions.some(
        (session) => session.sessionType === 'chemistry'
      ),
    });
  }

  return entries.sort(
    (left, right) =>
      left.displayName.localeCompare(right.displayName, undefined, {
        sensitivity: 'base',
      }) || left.coacheeUserId.localeCompare(right.coacheeUserId)
  );
}

/** A coachee's sessions grouped for display, plus the totals shown alongside them. */
export type CoacheeSessionSummary<S extends CoacheeSessionFields> = {
  /** Booked but not yet held, soonest first. */
  upcoming: S[];
  /** Waiting on the coach — flagged by an external calendar change, or unreported. */
  actionRequired: S[];
  /** Held sessions, most recent first. */
  history: S[];
  /** Canceled sessions, most recent first. */
  cancelled: S[];
  nextSession?: S;
  /** Delivered *coaching* sessions. Chemistry is counted separately as a status. */
  deliveredCount: number;
  /** Coaching time delivered, drawn from the coachee's coaching allocation. */
  deliveredMinutes: number;
};

function byStartTimeAscending(
  left: CoacheeSessionFields,
  right: CoacheeSessionFields
) {
  return Date.parse(left.startTime) - Date.parse(right.startTime);
}

function byStartTimeDescending(
  left: CoacheeSessionFields,
  right: CoacheeSessionFields
) {
  return Date.parse(right.startTime) - Date.parse(left.startTime);
}

/**
 * Minutes a session consumed, or zero when its timestamps are unusable.
 *
 * `resolveConfirmedDurationMinutes` throws on a session with no usable duration;
 * one malformed record should leave the total short, not break the page.
 */
function sessionMinutes(session: CoacheeSessionFields): number {
  try {
    return resolveConfirmedDurationMinutes(session);
  } catch {
    return 0;
  }
}

/** Groups one coachee's sessions for display and totals the coach's delivery. */
export function summarizeCoacheeSessions<S extends CoacheeSessionFields>(
  sessions: S[]
): CoacheeSessionSummary<S> {
  const upcomingKeys = new Set<CoachingSessionStatus>(
    UPCOMING_SCHEDULED_STATUSES
  );
  const deliveredKeys = new Set(COACH_DELIVERED_KEYS);

  const upcoming = sessions
    .filter((session) => upcomingKeys.has(session.status))
    .sort(byStartTimeAscending);
  const actionRequired = sessions
    .filter(coachSessionNeedsAction)
    .sort(byStartTimeDescending);
  const delivered = sessions.filter((session) =>
    deliveredKeys.has(session.status)
  );
  const deliveredCoaching = delivered.filter(
    (session) => session.sessionType === 'coaching'
  );

  return {
    upcoming,
    actionRequired,
    // Sessions needing the coach's attention get their own group, so they are not
    // also listed under history.
    history: sessions
      .filter(
        (session) =>
          isHistory(session.status) && !coachSessionNeedsAction(session)
      )
      .sort(byStartTimeDescending),
    cancelled: sessions
      .filter((session) => isCancelled(session.status))
      .sort(byStartTimeDescending),
    nextSession: upcoming[0],
    deliveredCount: deliveredCoaching.length,
    deliveredMinutes: deliveredCoaching.reduce(
      (total, session) => total + sessionMinutes(session),
      0
    ),
  };
}

/**
 * Chemistry is one call per partnership, so the dashboard shows a status rather
 * than a count. Cancelled chemistry sessions do not count as a held call.
 */
export type ChemistryStatus =
  | 'Session upcoming'
  | 'Decision pending'
  | 'Rejected'
  | 'Chosen';

function isHeldChemistry(session: CoacheeSessionFields): boolean {
  if (session.sessionType !== 'chemistry' || isCancelled(session.status)) {
    return false;
  }

  // `external_change_flagged` is history, but the booking is still live.
  return (
    isHistory(session.status) && session.status !== 'external_change_flagged'
  );
}

export function chemistryStatus(
  partnershipStatus: CoachingPartnershipStatus,
  sessions: CoacheeSessionFields[]
): ChemistryStatus {
  if (partnershipStatus === 'chosen') {
    return 'Chosen';
  }

  if (partnershipStatus === 'declined') {
    return 'Rejected';
  }

  const chemistrySessions = sessions.filter(
    (session) => session.sessionType === 'chemistry'
  );
  const upcomingKeys = new Set<CoachingSessionStatus>(
    UPCOMING_SCHEDULED_STATUSES
  );

  if (chemistrySessions.some((session) => upcomingKeys.has(session.status))) {
    return 'Session upcoming';
  }

  if (chemistrySessions.some(isHeldChemistry)) {
    return 'Decision pending';
  }

  return 'Session upcoming';
}
